import { useEffect, useMemo, useState } from 'react'
import { useAuth } from '../../context/AuthContext'
import { useCatalog } from '../../context/CatalogContext'
import { DEFAULT_ACCENT, fetchPublicMenu } from '../../services/publicMenu'
import { renewTableQrToken, tableQrUrl } from '../../services/tableQr'
import {
  QR_CARD_MM,
  QR_TEMPLATES,
  qrPreviewHtml,
  qrPrintHtml,
  type QrPrintItem,
  type QrTemplate,
} from '../../utils/tableQrPrint'
import { ConfirmDialog, type ConfirmSpec } from '../pos/ConfirmDialog'
import { CheckIcon, ExternalLinkIcon, PrinterIcon, RefreshIcon } from '../icons'
import './TableQrPanel.css'

const TPL_KEY = 'adissoo.qrTemplate'

/** Önizleme kutusu (px); kart bu kutuya sığacak kadar küçültülüyor. */
const BOX = { w: 220, h: 150 }
const PX_PER_MM = 3.78

/**
 * Ayarlar › QR Menü › Masa karekodları (QR Sipariş, 30 Eki 2026). Her masanın
 * kendi kodu var; müşteri okutunca o masadan sipariş veriyor. Üç hazır
 * şablon (kullanıcı): masa çadırı, kare etiket, renkli kart.
 */
export function TableQrPanel({ tenantId }: { tenantId: string }) {
  const { tenantName } = useAuth()
  const { activeTables: tables, halls } = useCatalog()
  const [accent, setAccent] = useState(DEFAULT_ACCENT)
  const [tpl, setTpl] = useState<QrTemplate>(() => {
    try {
      const v = localStorage.getItem(TPL_KEY)
      return v === 'sticker' || v === 'card' || v === 'tent' ? v : 'card'
    } catch {
      return 'card'
    }
  })
  const [picked, setPicked] = useState<Set<string> | null>(null)
  const [ask, setAsk] = useState<ConfirmSpec | null>(null)
  const [note, setNote] = useState<string | null>(null)

  useEffect(() => {
    let live = true
    fetchPublicMenu(tenantId)
      .then((m) => live && m?.style.accent && setAccent(m.style.accent))
      .catch(() => {})
    return () => {
      live = false
    }
  }, [tenantId])

  const active = useMemo(() => tables.filter((t) => t.active && t.qrToken), [tables])
  // Varsayılan: bütün masalar seçili.
  const sel = picked ?? new Set(active.map((t) => t.id))
  const hallName = (id?: string) => halls.find((h) => h.id === id)?.name
  const groups = useMemo(() => {
    const out: { id: string; name: string; tables: typeof active }[] = []
    for (const h of halls) {
      const list = active.filter((t) => t.hallId === h.id)
      if (list.length) out.push({ id: h.id, name: h.name, tables: list })
    }
    const rest = active.filter((t) => !t.hallId || !halls.some((h) => h.id === t.hallId))
    if (rest.length) out.push({ id: '', name: halls.length ? 'Salonsuz' : 'Masalar', tables: rest })
    return out
  }, [active, halls])

  const items: QrPrintItem[] = active
    .filter((t) => sel.has(t.id))
    .map((t) => ({ name: t.name, hall: hallName(t.hallId), url: tableQrUrl(tenantId, t.qrToken!) }))
  const brand = { business: tenantName, accent }
  const sample: QrPrintItem = items[0] ?? {
    name: active[0]?.name ?? 'Masa 1',
    hall: hallName(active[0]?.hallId),
    url: active[0]?.qrToken ? tableQrUrl(tenantId, active[0].qrToken) : `${window.location.origin}/m/${tenantId}`,
  }

  const toggle = (ids: string[], on: boolean) => {
    const next = new Set(sel)
    for (const id of ids) {
      if (on) next.add(id)
      else next.delete(id)
    }
    setPicked(next)
  }

  const choose = (t: QrTemplate) => {
    setTpl(t)
    try {
      localStorage.setItem(TPL_KEY, t)
    } catch {
      // Seçim yalnızca bu açılışta kalır.
    }
  }

  const print = () => {
    const w = window.open('', '_blank')
    if (!w) {
      setNote('Yazdırma penceresi açılamadı. Tarayıcının açılır pencere iznini kontrol edin.')
      return
    }
    w.document.open()
    w.document.write(qrPrintHtml(tpl, items, brand))
    w.document.close()
  }

  const renew = () =>
    setAsk({
      title: `${items.length} masanın kodu yenilensin mi?`,
      body: 'Eski karekodlar artık çalışmaz; yeni kodları basıp masalara koymanız gerekir. Kod fotoğraflanıp masa dışından kullanılıyorsa işe yarar.',
      confirmLabel: 'Kodları yenile',
      danger: true,
      onConfirm: async () => {
        try {
          for (const t of active.filter((x) => sel.has(x.id))) await renewTableQrToken(t.id)
          setNote('Kodlar yenilendi. Yeni kodları yazdırmayı unutmayın.')
        } catch {
          setNote('Yenilenemedi. İnternet bağlantısını kontrol edin.')
        }
      },
    })

  const perPage = QR_TEMPLATES.find((x) => x.id === tpl)!.perPage
  const pages = Math.ceil(items.length / perPage)

  if (active.length === 0) {
    return <p className="tqr__empty">Henüz masa yok. Masalar sayfasından masa ekleyince kodları burada çıkar.</p>
  }

  return (
    <div className="tqr">
      <p className="qrm__lead">
        Her masanın kendi kodu var. Müşteri okutunca menüyü görür, o masadan sipariş verir, garson çağırır, hesap
        ister.
      </p>

      <div className="tqr__tpls" role="radiogroup" aria-label="Şablon">
        {QR_TEMPLATES.map((t) => {
          const mm = QR_CARD_MM[t.id]
          const w = mm.w * PX_PER_MM + 24
          const h = mm.h * PX_PER_MM + 24
          const scale = Math.min(BOX.w / w, BOX.h / h)
          return (
            <button
              key={t.id}
              type="button"
              role="radio"
              aria-checked={tpl === t.id}
              className={`tqr__tpl ${tpl === t.id ? 'is-on' : ''}`}
              onClick={() => choose(t.id)}
            >
              <span className="tqr__prev" aria-hidden="true">
                <iframe
                  title={t.name}
                  tabIndex={-1}
                  srcDoc={qrPreviewHtml(t.id, sample, brand)}
                  style={{ width: w, height: h, transform: `scale(${scale})` }}
                />
              </span>
              <span className="tqr__tpl-text">
                <b>
                  {t.name}
                  {tpl === t.id && (
                    <span className="tqr__check">
                      <CheckIcon />
                    </span>
                  )}
                </b>
                <small>{t.hint}</small>
                <small className="tqr__per">A4'e {t.perPage} tane</small>
              </span>
            </button>
          )
        })}
      </div>

      <div className="tqr__pick">
        <div className="tqr__pick-head">
          <span className="tqr__label">Masalar</span>
          <button
            type="button"
            className="tqr__all"
            onClick={() => toggle(active.map((t) => t.id), sel.size !== active.length)}
          >
            {sel.size === active.length ? 'Hiçbirini seçme' : 'Hepsini seç'}
          </button>
        </div>
        {groups.map((g) => {
          const ids = g.tables.map((t) => t.id)
          const all = ids.every((id) => sel.has(id))
          return (
            <div key={g.id} className="tqr__group">
              {groups.length > 1 && (
                <button type="button" className={`tqr__hall ${all ? 'is-on' : ''}`} onClick={() => toggle(ids, !all)}>
                  {g.name}
                </button>
              )}
              <div className="tqr__chips">
                {g.tables.map((t) => {
                  const on = sel.has(t.id)
                  return (
                    <button
                      key={t.id}
                      type="button"
                      aria-pressed={on}
                      className={`tqr__chip ${on ? 'is-on' : ''}`}
                      onClick={() => toggle([t.id], !on)}
                    >
                      {on && <CheckIcon />}
                      {t.name}
                    </button>
                  )
                })}
              </div>
            </div>
          )
        })}
      </div>

      {note && <p className="tqr__note">{note}</p>}

      <div className="tqr__actions">
        <button type="button" className="btn btn--primary tqr__print" onClick={print} disabled={items.length === 0}>
          <PrinterIcon />
          Yazdır: {items.length} masa{pages > 0 && `, ${pages} sayfa`}
        </button>
        <a
          className="btn btn--outline"
          href={sample.url}
          target="_blank"
          rel="noreferrer"
          aria-disabled={items.length === 0}
        >
          <ExternalLinkIcon />
          {sample.name} kodunu dene
        </a>
        <button type="button" className="btn btn--ghost tqr__renew" onClick={renew} disabled={items.length === 0}>
          <RefreshIcon />
          Kodları yenile
        </button>
      </div>

      {ask && <ConfirmDialog {...ask} onClose={() => setAsk(null)} />}
    </div>
  )
}
